import React from "react";
import { useForm, Head } from "@inertiajs/react";
import MainLayout from "../../components/MainLayout";
import { StoreSettings } from "../../types";

interface NewProps {
  errors: any;
  store_settings: StoreSettings;
  exclusive_products: any[];
}

export default function New({ errors, store_settings, exclusive_products }: NewProps) {
  const { data, setData, post, processing } = useForm({
    name: "",
    address: "",
    document_type: "DNI",
    document_number: "",
    phone: "",
    email: "",
    is_minor: false,
    parent_name: "",
    parent_document_type: "DNI",
    parent_document_number: "",
    complaint_type: "reclamo", // 'reclamo' o 'queja'
    product_or_service_description: "",
    details: "",
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    post("/complaints");
  }

  return (
    <MainLayout store_settings={store_settings} exclusiveProducts={exclusive_products}>
      <Head title="Libro de Reclamaciones" />
      <div className="container mx-auto max-w-3xl p-8 my-8 border rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold mb-2 text-center">
          Libro de Reclamaciones
        </h1>
        <p className="text-center text-gray-600 mb-6">
          Conforme a lo establecido en el Código de Protección y Defensa del
          Consumidor
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <fieldset className="border p-4 rounded-md">
            <legend className="font-semibold px-2">
              1. Identificación del Consumidor
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Nombre Completo
                </label>
                <input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
                {errors.name && (
                  <div className="text-red-500 text-xs mt-1">{errors.name}</div>
                )}
              </div>
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700"
                >
                  Domicilio
                </label>
                <input
                  id="address"
                  value={data.address}
                  onChange={(e) => setData("address", e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
                {errors.address && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.address}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="document_type"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Tipo Doc.
                  </label>
                  <select
                    id="document_type"
                    value={data.document_type}
                    onChange={(e) => setData("document_type", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option>DNI</option>
                    <option>C.E.</option>
                    <option>Pasaporte</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="document_number"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Nro. Documento
                  </label>
                  <input
                    id="document_number"
                    value={data.document_number}
                    onChange={(e) => setData("document_number", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                  {errors.document_number && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.document_number}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700"
                >
                  Teléfono
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => setData("phone", e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                />
                {errors.email && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.email}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4">
              <label htmlFor="is_minor" className="flex items-center">
                <input
                  id="is_minor"
                  type="checkbox"
                  checked={data.is_minor}
                  onChange={(e) => setData("is_minor", e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500"
                />
                <span className="ml-2 text-sm text-gray-600">
                  Soy menor de edad
                </span>
              </label>
            </div>
          </fieldset>

          {data.is_minor && (
            <fieldset className="border p-4 rounded-md">
              <legend className="font-semibold px-2">
                2. Identificación del Padre/Madre o Apoderado
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div>
                  <label
                    htmlFor="parent_name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Nombre Completo del Apoderado
                  </label>
                  <input
                    id="parent_name"
                    value={data.parent_name}
                    onChange={(e) => setData("parent_name", e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required={data.is_minor}
                  />
                  {errors.parent_name && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.parent_name}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="parent_document_type"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Tipo Doc.
                    </label>
                    <select
                      id="parent_document_type"
                      value={data.parent_document_type}
                      onChange={(e) =>
                        setData("parent_document_type", e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option>DNI</option>
                      <option>C.E.</option>
                      <option>Pasaporte</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="parent_document_number"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Nro. Documento
                    </label>
                    <input
                      id="parent_document_number"
                      value={data.parent_document_number}
                      onChange={(e) =>
                        setData("parent_document_number", e.target.value)
                      }
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required={data.is_minor}
                    />
                    {errors.parent_document_number && (
                      <div className="text-red-500 text-xs mt-1">
                        {errors.parent_document_number}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </fieldset>
          )}

          <fieldset className="border p-4 rounded-md">
            <legend className="font-semibold px-2">
              {data.is_minor ? "3" : "2"}. Identificación del bien contratado
            </legend>
            <div className="space-y-2 mt-2">
              <div className="flex items-center gap-x-4">
                <label className="block text-sm font-medium text-gray-700">
                  Tipo:
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="complaint_type"
                    value="reclamo"
                    checked={data.complaint_type === "reclamo"}
                    onChange={(e) => setData("complaint_type", e.target.value)}
                    className="text-indigo-600"
                  />
                  <span className="ml-2">Reclamo</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="complaint_type"
                    value="queja"
                    checked={data.complaint_type === "queja"}
                    onChange={(e) => setData("complaint_type", e.target.value)}
                    className="text-indigo-600"
                  />
                  <span className="ml-2">Queja</span>
                </label>
              </div>
              <p className="text-xs text-gray-500">
                RECLAMO: Disconformidad relacionada a los productos o servicios.
                QUEJA: Disconformidad no relacionada a los productos o
                servicios; o, malestar o descontento respecto a la atención al
                público.
              </p>
              <div>
                <label
                  htmlFor="product_or_service_description"
                  className="block text-sm font-medium text-gray-700"
                >
                  Descripción del Producto o Servicio
                </label>
                <textarea
                  id="product_or_service_description"
                  value={data.product_or_service_description}
                  onChange={(e) =>
                    setData("product_or_service_description", e.target.value)
                  }
                  rows={2}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                ></textarea>
                {errors.product_or_service_description && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.product_or_service_description}
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          <fieldset className="border p-4 rounded-md">
            <legend className="font-semibold px-2">
              {data.is_minor ? "4" : "3"}. Detalle de la Reclamación
            </legend>
            <div>
              <label
                htmlFor="details"
                className="block text-sm font-medium text-gray-700"
              >
                Describe tu reclamo o queja
              </label>
              <textarea
                id="details"
                value={data.details}
                onChange={(e) => setData("details", e.target.value)}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              ></textarea>
              {errors.details && (
                <div className="text-red-500 text-xs mt-1">
                  {errors.details}
                </div>
              )}
            </div>
          </fieldset>

          <div className="text-center">
            <button
              type="submit"
              disabled={processing}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {processing ? "Enviando..." : "Enviar Reclamo"}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}

